import { useState } from 'react'

import SaveIcon from '@mui/icons-material/Save'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'

import type { DisciplineVerzameling } from '@models/DisciplineVerzameling'

import { useAuthenticatedUser } from '../../../../auth/hooks/useAuthenticatedUser'
import { SelectObject } from '../../../../core/components/SelectObject'
import { useSaveEvent } from '../../hooks/useSaveEvent'
import type { HoofdleidingProject } from '../../types/HoofdleidingProject'

interface Props {
  project: HoofdleidingProject
  handleClose: () => void
}

export const NetbeheerderTracelengte = ({ project, handleClose }: Props) => {
  const { mutate: saveEvent, isPending } = useSaveEvent()
  const { organisatie } = useAuthenticatedUser()
  const [disciplineVerzameling, setDisciplineVerzameling] =
    useState<DisciplineVerzameling | null>(null)
  const [tracelengte, setTracelengte] = useState<number | ''>('')

  const [errors, setErrors] = useState({
    disciplineError: false,
    tracelengteError: false,
  })

  const disciplineOptions = project.disciplineVerzamelingen.filter(
    (d) =>
      d.netbeheerderLabel === organisatie ||
      project.verantwoordelijken.includes(organisatie),
  )

  const handleSubmitClick = () => {
    if (!disciplineVerzameling) {
      setErrors({
        ...errors,
        disciplineError: true,
      })
    } else {
      if (tracelengte === '' || tracelengte < 0) {
        setErrors({
          ...errors,
          disciplineError: false,
          tracelengteError: true,
        })
        return
      }

      saveEvent(
        {
          id: project.aanvraagID,
          data: {
            discipline: disciplineVerzameling.disciplineLabel,
            tracelengte: tracelengte,
            netbeheerder: disciplineVerzameling.netbeheerderLabel,
          },
          type: 'hoofdleiding_tracelengte',
          source: 'manual',
        },
        {
          onSuccess: () => handleClose(),
        },
      )
    }
  }

  const handleDisciplineChange = (value: DisciplineVerzameling | null) => {
    setDisciplineVerzameling(value)
    /* v8 ignore next - TODO: missing coverage -- @preserve */
    setTracelengte(value?.tracelengte || '')
  }

  return (
    <>
      <DialogTitle>Tracélengtes wijzigen</DialogTitle>
      <DialogContent>
        <Box sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}>
          <SelectObject
            options={disciplineOptions}
            onChange={handleDisciplineChange}
            value={disciplineVerzameling}
            getOptionLabel={(dv) =>
              // als er maar 1 netbeheerder is, is NB label niet nodig, dus toon alleen disciplcine
              disciplineOptions.every(
                (x) => x.netbeheerderLabel === organisatie,
              )
                ? /* v8 ignore next - TODO: missing coverage -- @preserve */
                  dv.disciplineLabel
                : `${dv.netbeheerderLabel} - ${dv.disciplineLabel}`
            }
            label="Discipline"
            error={errors.disciplineError}
          />
          <TextField
            sx={{ width: '100%' }}
            variant="standard"
            value={tracelengte}
            label="Tracélengte"
            type="number"
            onChange={(e) =>
              setTracelengte(
                /* v8 ignore next - TODO: missing coverage -- @preserve */
                e.target.value === '' ? '' : Number(e.target.value),
              )
            }
            error={errors.tracelengteError}
            slotProps={{ htmlInput: { min: 0 } }}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary" autoFocus>
          Annuleren
        </Button>
        <Button
          color="primary"
          onClick={handleSubmitClick}
          variant="contained"
          startIcon={
            isPending ? (
              /* v8 ignore next - TODO: missing coverage -- @preserve */ <CircularProgress
                size={20}
              />
            ) : (
              <SaveIcon />
            )
          }
        >
          Tracélengtes wijzigen
        </Button>
      </DialogActions>
    </>
  )
}
